// src/shared/hooks/useGameSession.js
// Shared utilities: offline detection, session persistence for resume, confirm-leave modal, room sound cues
import { useState, useEffect, useCallback, useRef } from 'react';
import { GAME_STATE_KEYS } from '../GameEngine';
import { playGameSound } from '../audio/gameAudio';
import { setPlayerOnlineStatus } from '../firebase/services';

const SESSION_KEY = 'game_session_v1';

// ── Persist / restore session ─────────────────────────────────────────────────
export function saveSession(roomId, userId, gameType) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ roomId, userId, gameType, ts: Date.now() }));
  } catch {}
}

export function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

export function getStoredSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    // Expire after 24 h
    if (Date.now() - s.ts > 86_400_000) { localStorage.removeItem(SESSION_KEY); return null; }
    return s;
  } catch { return null; }
}

// ── Online / offline hook ─────────────────────────────────────────────────────
export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on  = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

// ── Reconnect hook: re-registers Firebase presence when coming back online ─────
// Used inside every game component via useGameGuard
function useReconnect(roomId, userId) {
  const online = useOnline();
  const prevOnline = useRef(online);

  useEffect(() => {
    if (!roomId || !userId) return;
    const wasOffline = prevOnline.current === false;
    prevOnline.current = online;

    if (online && wasOffline) {
      // Back online — re-register presence and reset onDisconnect handler
      setPlayerOnlineStatus(roomId, userId, true).catch(() => {});
    }
  }, [online, roomId, userId]);

  return online;
}

// ── Hook used inside every game component ─────────────────────────────────────
export function useGameGuard({ roomId, userId, gameType, leaveCallback }) {
  const online = useReconnect(roomId, userId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Persist session whenever we're in a room
  useEffect(() => {
    if (roomId && userId) saveSession(roomId, userId, gameType);
  }, [roomId, userId, gameType]);

  const requestLeave = useCallback(() => setConfirmOpen(true), []);
  const cancelLeave  = useCallback(() => setConfirmOpen(false), []);
  const confirmLeave = useCallback(async () => {
    clearSession();
    setConfirmOpen(false);
    await leaveCallback();
  }, [leaveCallback]);

  return { online, confirmOpen, requestLeave, cancelLeave, confirmLeave };
}

// ── Sound cues ────────────────────────────────────────────────────────────────
function describeRoomAudioState(roomId, room) {
  const gameStateKey = GAME_STATE_KEYS[room.gameType];
  const gameState = gameStateKey ? room[gameStateKey] || {} : {};
  return {
    roomId,
    gameType: room.gameType,
    status: room.status,
    round: room.currentRound ?? gameState.currentHoleIdx ?? gameState.currentQuestionIndex,
    phase: gameState.phase,
    turn: room.currentDrawer ?? gameState.currentTurn ?? gameState.currentTurnIndex ??
      gameState.currentIndex ?? gameState.currentPlayer ?? gameState.currentPlayerId,
    dice: gameState.diceValue ?? gameState.lastRoll,
    winner: gameState.winner,
    lastAction: JSON.stringify(gameState.lastAction || null),
    activity: room.gameType === 'boggle' ? null : JSON.stringify(
      gameState.positions || gameState.pieces || gameState.balls ||
      gameState.submissions || gameState.answers ||
      gameState.discardPile?.length || gameState.turnCount || null
    ),
    scores: JSON.stringify(gameState.scores || Object.values(room.players || {}).map(player => player.score || 0)),
  };
}

/** Plays shared sound cues when the room's game state changes between snapshots. */
export function useRoomAudioCues(roomId, room) {
  const previousAudioState = useRef(null);

  useEffect(() => {
    if (!roomId || !room) {
      previousAudioState.current = null;
      return;
    }

    const current = describeRoomAudioState(roomId, room);
    const previous = previousAudioState.current;
    previousAudioState.current = current;
    if (!previous || previous.roomId !== roomId || previous.gameType !== room.gameType) return;
    if (previous.status !== current.status) {
      if (current.status === 'finished' || current.winner) playGameSound('win');
      else if (current.status === 'playing') playGameSound('start');
      else if (current.status === 'roundEnd') playGameSound('move');
      return;
    }
    if (previous.round !== current.round) {
      playGameSound('start');
    } else if (previous.winner !== current.winner && current.winner) {
      playGameSound('win');
    } else if (previous.dice !== current.dice && current.dice != null) {
      playGameSound('roll');
    } else if (previous.lastAction !== current.lastAction && current.lastAction) {
      playGameSound('card');
    } else if (previous.scores !== current.scores) {
      playGameSound('correct');
    } else if (previous.phase !== current.phase && current.phase) {
      playGameSound('start');
    } else if (previous.activity !== current.activity) {
      playGameSound(room.gameType === 'uno' ? 'card' : 'move');
    } else if (previous.turn !== current.turn && current.turn != null) {
      playGameSound('move');
    }
  }, [roomId, room]);
}
